/**
 * unifiedInboxFormat — PURE display formatting for the Unified Inbox
 * `DataTable` columns (M-UX-5.2). Kept separate from the mapping layer
 * (`utils/unifiedInbox/mappers.ts`, W5.1) — these are presentation-only
 * concerns (locale formatting), not data normalization.
 *
 * @module components/inbox/unifiedInboxFormat
 */

import { formatDate } from '../../utils/formatters';

/**
 * Formats integer Cents as a locale currency string. `null` (§6.2: "NIE 0
 * raten") renders as an em dash — NEVER `0,00 €`.
 *
 * @param cents integer cents, or `null` when unknown.
 * @param currency ISO currency code; `''`/invalid falls back to EUR.
 */
export function formatUnifiedAmount(cents: number | null, currency: string): string {
  if (cents === null) return '—';
  const euros = cents / 100;
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(euros);
  } catch {
    return `${euros.toFixed(2)} ${currency || 'EUR'}`;
  }
}

/**
 * Formats an epoch-ms timestamp as a locale date via the central
 * {@link formatDate} (M-A, architecture-ux-density-v2.md §5.6/§14). `0`
 * (§6.3 edge-case: unknown/missing `receivedAt`, sorted to the list's end)
 * renders as an em dash — `formatDate` already treats falsy/unparsable input
 * this way, so this wrapper is now a thin pass-through kept for call-site
 * stability (column `accessor`s import this module, not `formatDate` itself).
 *
 * @param ms epoch-ms, or `0` when unknown.
 */
export function formatUnifiedDate(ms: number): string {
  return formatDate(ms || null);
}
